import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Grid,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
} from '@material-ui/core';
import Modal from 'components/Modal';
import { useStyles } from '@webapps/styles';
import { cx } from '@emotion/css';
import CheckIcon from '@material-ui/icons/Check';
import { PaginationNumbers } from '@webapps/reactjs-tech-components';
import { getAbsenceByStudentIds } from '@webapps/business';

const months = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const stylesFn = ({ spacing, palette }) => ({
  content: {
    // padding: 12,
    backgroundColor: "white",
  },
  formControl: {
    width: '100%',
  },
  title: {
    paddingBottom: spacing(2),
  },
  studentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing(2),
    justifyContent: 'space-between',
  },
  studentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: spacing(2),
    marginBottom: spacing(0),
    width: `calc(50% - ${spacing(1)}px)`,
    backgroundColor: "#F3F7FF",
    borderRadius: spacing(1),
    border: "1px solid transparent",
  },
  selectedStudent: {
    border: "1px solid #0060F9 !important",
  },
  studentInfo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContainer: {
    marginLeft: spacing(2),
  },
  periodSection: {
    marginTop: spacing(4),
  },
  dates: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(0.25),
  },
  checkboxContainer: {
    minWidth: 20,
    minHeight: 20,
    width: 20,
    height: 20,
    border: '1px solid #0060F9',
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    cursor: 'pointer',
    '&.checked': {
      backgroundColor: '#0060F9',
    },
  },
  checkIcon: {
    color: 'white',
    fontSize: 16,
    width: 16,
    height: 16,
  },
  selectAllContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing(1),
    marginTop: spacing(2),
  },
});

const ExportStatementsModal = ({
  open,
  onClose,
  students,
  setSelectedStudentsForExport,
  setShowStatements,
  setAbsencesData,
  startMonth,
  startYear,
  endMonth,
  endYear,
  setStartMonth,
  setStartYear,
  setEndMonth,
  setEndYear,
  handleDownloadStatement,
  htmlObjectForExport = [],
  setHtmlObjectForExport,
  exportHtmlsRef
}) => {
  const { classes, global } = useStyles(stylesFn);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStudentsObjectsForExport, setSelectedStudentsObjectsForExport] = useState([])
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 6;

  useEffect(() => {
    if (open) {
      setSelectedStudents([]);
      setSelectAll(false);
      setHtmlObjectForExport([]);
    }
  }, [open, setHtmlObjectForExport]);

  const handleToggleAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student.student.id));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleStudent = (studentId) => {
    const currentIndex = selectedStudents.indexOf(studentId);
    const newSelectedStudents = [...selectedStudents];

    if (currentIndex === -1) {
      newSelectedStudents.push(studentId);
    } else {
      newSelectedStudents.splice(currentIndex, 1);
    }

    setSelectedStudents(newSelectedStudents);
    setSelectAll(newSelectedStudents.length === students.length);
  };

  const handlePageChange = (event) => {
    setCurrentPage(event);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedStudents = students.slice(startIndex, endIndex);

  const handleExport = async () => {

    let startDate = new Date(startYear, startMonth, 1);
    startDate.setUTCDate(1);
    startDate.setUTCHours(0, 0, 0, 0);
    startDate = startDate.toISOString().split('T')[0];

    const endDateObj = new Date(endYear, endMonth, 0);

    const endDate = endDateObj.getFullYear() + '-' +
      String(endDateObj.getMonth() + 1).padStart(2, '0') + '-' +
      String(endDateObj.getDate()).padStart(2, '0');



    // Find the full student objects for the selected IDs
    const selectedStudentObjects = students
      .filter(item => selectedStudents.includes(item.student.id))
      .map(item => ({
        ...item.student,
        selected: true,
        startDate: startDate,
        endDate: endDate,
        dateInterval: {
          startDate: startDate,
          endDate: endDate
        }
      }));

    // setSelectedStudentsForExport(selectedStudentObjects);
    // setSelectedStudentsObjectsForExport(selectedStudentObjects);
    const joinStudentIds = selectedStudentObjects.map(student => student.id).join(",");

    setIsLoading(true);

    try {
      // Appel à l'API pour récupérer les absences
      const response = await getAbsenceByStudentIds.query({
        studentIds: joinStudentIds,
        startDate: startDate,
        endDate: endDate
      });

      const data = getAbsenceByStudentIds.format(response);

      // Store the absences data
      setAbsencesData(data);

      // Show the statements
      setShowStatements(true);

      // Wait for statements to render and resources to load
      setTimeout(() => {
        // Generate and export PDFs
        exportStatements(data, startDate, endDate, selectedStudentObjects);
      }, 3000); // Increased timeout to ensure resources are loaded

    } catch (error) {
      console.error("Erreur lors de l'export des relevés :", error);
      setIsLoading(false);
    }
  };

  const exportOne = (student) => {
    const onClose = () => {
      setIsLoading(false);
      setShowStatements(false);
      // Ensure htmlObjectForExport is reset even in case of error
      setHtmlObjectForExport([]);
    };
    try {
      handleDownloadStatement(student, onClose);
    } catch {
      setIsLoading(false);
      setShowStatements(false);
      // Ensure htmlObjectForExport is reset even in case of error
      setHtmlObjectForExport([]);
    }
  }

  const exportMany = async (data, startDate, endDate, selectedStudentsObjects) => {
    try {
      // Use the ref to get the most up-to-date htmls data
      const currentHtmls = [...(exportHtmlsRef.current || [])];
      setHtmlObjectForExport([]);

      // Get the student IDs from the data
      const studentIds = selectedStudents;

      // Check for duplicate studentIds and keep only the last occurrence
      const uniqueHtmls = [];
      const processedIds = new Set();

      // Process in reverse to keep the last occurrence
      for (let i = currentHtmls.length - 1; i >= 0; i--) {
        const item = currentHtmls[i];
        if (!processedIds.has(item.studentId)) {
          uniqueHtmls.unshift(item); // Add to beginning to maintain original order
          processedIds.add(item.studentId);
        }
      }



      // Call the API to export statements
      const exportResponse = await fetch(`${process.env.NX_PROXY_API}/export-students-statement`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          absences: data,
          studentIds,
          startDate,
          endDate,
          htmls: uniqueHtmls // Use the filtered htmls
        }),
      });


      if (!exportResponse.ok) {
        const errorText = await exportResponse.text();
        throw new Error(`Erreur lors de l'export: ${exportResponse.status} - ${errorText}`);
      }

      let classroomLabel = selectedStudentsObjects[0]?.classroom?.label
      classroomLabel = classroomLabel ? classroomLabel : ''

      const formattedStartDate = startDate.slice(0, 7).replace(/-/g, '_');
      const formattedEndDate = endDate.slice(0, 7).replace(/-/g, '_');
      let downloadName = `releves_absences_${classroomLabel}_${formattedStartDate}-${formattedEndDate}.zip`
      downloadName = downloadName.replace(/ /g, '_');
      const blob = await exportResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = studentIds.length > 1 ? downloadName : 'releve_absence.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Reset states and close modal
      setIsLoading(false);
      setShowStatements(false);
      onClose();
      // htmlObjectForExport already reset at the beginning
    } catch (error) {
      console.error("Erreur lors de l'export des relevés :", error);
      // alert(`Erreur lors de l'export des relevés : ${error.message}`);
      setIsLoading(false);
      setShowStatements(false);
      // Ensure htmlObjectForExport is reset even in case of error
      setHtmlObjectForExport([]);
    }
  };

  const exportStatements = (data, startDate, endDate, selectedStudentObjects) => {

    if (selectedStudentObjects?.length === 1) {

      exportOne(selectedStudentObjects[0])
    } else {

      exportMany(data, startDate, endDate, selectedStudentObjects);
    }

  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Exporter les relevés d'absences"
      width={800}
      fullWidth
      styles={
        {
          padding: '20px 24px'
        }
      }
    >
      <div className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className={cx(global.title, classes.title)}>
              Sélectionner les étudiants
            </div>

            <div className={classes.selectAllContainer}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  className={`${classes.checkboxContainer} ${selectAll ? 'checked' : ''}`}
                  onClick={handleToggleAll}
                  style={{ flexShrink: 0, marginRight: 8 }}
                >
                  {selectAll && (
                    <CheckIcon className={classes.checkIcon} />
                  )}
                </div>
                <Typography>Sélectionner tous les étudiants</Typography>
              </div>
              <Typography variant="body2">
                {selectedStudents.length} sur {students.length} sélectionnés
              </Typography>
            </div>

            <div className={classes.studentContainer}>
              {displayedStudents.map((item) => (
                <div
                  key={item.student.id}
                  className={`${classes.studentItem} ${selectedStudents.includes(item.student.id) ? classes.selectedStudent : ''
                    }`}
                  onClick={() => handleToggleStudent(item.student.id)}
                >
                  <div className={classes.studentInfo}>
                    <div className={classes.infoContainer}>
                      <div className={cx(global.title)}>
                        {item.student.firstname} {item.student.lastname}
                      </div>
                      <div className={cx(global.infoText)}>
                        {item.student.email || 'Email non spécifié'}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`${classes.checkboxContainer} ${selectedStudents.includes(item.student.id) ? 'checked' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStudent(item.student.id);
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    {selectedStudents.includes(item.student.id) && (
                      <CheckIcon className={classes.checkIcon} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Box mt={2} display="flex" justifyContent="center">
              <PaginationNumbers
                page={currentPage}
                pageCount={itemsPerPage}
                totalCount={students.length}
                onChange={handlePageChange}
              />
            </Box>
          </Grid>

          <Grid item xs={12} className={classes.periodSection}>
            <div className={cx(global.title, classes.title)}>
              Période
            </div>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Date de début
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Mois</InputLabel>
                      <Select
                        value={startMonth}
                        onChange={(e) => setStartMonth(e.target.value)}
                      >
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Année</InputLabel>
                      <Select
                        value={startYear}
                        onChange={(e) => setStartYear(e.target.value)}
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Date de fin
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Mois</InputLabel>
                      <Select
                        value={endMonth}
                        onChange={(e) => setEndMonth(e.target.value)}
                      >
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Année</InputLabel>
                      <Select
                        value={endYear}
                        onChange={(e) => setEndYear(e.target.value)}
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <div className={classes.buttonContainer}>
          <Button
            onClick={onClose}
            variant="outlined"
            style={{ borderColor: '#0060F9', color: '#0060F9' }}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={selectedStudents.length === 0 || isLoading}
          >
            {isLoading ? 'Chargement...' : 'Exporter'}
          </Button>
        </div>


      </div>
    </Modal>
  );
};

export default ExportStatementsModal;
